import { useQuery, useMutation, UseQueryOptions, UseMutationOptions, QueryKey } from '@tanstack/react-query'
import Api from '@vacationtracker/shared/services/api'

/**
 * Generic hook for REST API GET requests using TanStack Query
 *
 * @param queryKey - Unique key for the query (for caching)
 * @param url - REST API endpoint URL
 * @param options - Additional TanStack Query options
 *
 * @example
 * const { data, isLoading, error } = useRestQuery(
 *   queryKeys.rechargeType.gameColleagues(10),
 *   '/core/game/colleagues?limit=10'
 * )
 */
export function useRestQuery<TData = unknown, TError = Error>(queryKey: QueryKey, url: string, options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'>) {
  return useQuery<TData, TError>({
    queryKey,
    queryFn: async () => {
      const data = await Api.get<TData>(url)
      return data
    },
    ...options,
  })
}

/**
 * Generic hook for REST API POST requests using TanStack Query
 *
 * @param url - REST API endpoint URL
 * @param options - Additional TanStack Query mutation options
 *
 * @example
 * const { mutate, isLoading } = useRestMutation(
 *   '/core/save-recharge-type',
 *   {
 *     onSuccess: () => {
 *       queryClient.invalidateQueries(queryKeys.rechargeType.all)
 *     }
 *   }
 * )
 *
 * // Later in your component:
 * mutate({ rechargeType: 'SOCIAL_SPRINTER', quizAnswers: {...} })
 */
export function useRestMutation<TData = unknown, TVariables = Record<string, unknown>, TError = Error>(url: string, options?: UseMutationOptions<TData, TError, TVariables>) {
  return useMutation<TData, TError, TVariables>({
    mutationFn: async (variables: TVariables) => {
      return Api.post<TVariables, TData>(url, variables)
    },
    ...options,
  })
}
